import { format } from 'owa-localize';
import createItemIdForRequest from './createItemIdForRequest';
import getAddSmimePropertiesRequestObject from './getAddSmimePropertiesRequestObject';
import getDataUriCount from './getDataUriCount';
import { getDocLinks } from './getDocLinks';
import getRecipientsFromWellViewState from './getRecipientsFromWellViewState';
import removeSrcAttributeFromInlineImages from './removeSrcAttributeFromInlineImages';
import setFromInfoToMessage from './setFromInfoToMessage';
import setFromInfoToPost from './setFromInfoToPost';
import tryUpdateExtendedPropertiesForAppendOnSend from './tryUpdateExtendedPropertiesForAppendOnSend';
import tryUpdateExtendedPropertiesForInternetHeaders from './tryUpdateExtendedPropertiesForInternetHeaders';
import { isGroupScenario } from './viewStateUtils';
import { preHandleErrorResponse } from './wasclUtils';
import clearKeysOfInternetHeadersToBeRemoved from '../actions/clearKeysOfInternetHeadersToBeRemoved';
import checkIfResponseSuccess from 'owa-mail-store/lib/services/utils/checkIfResponseSuccess';
import updateComposeItemId from '../actions/updateComposeItemId';
import updateInternetMessageId from '../actions/updateInternetMessageId';
import { findComposePlugins } from '../plugins/ComposePlugins';
import {
    ComposeViewState,
    ComposeOperation,
    TextDirection,
    GroupComposeViewState,
    PublicFolderComposeViewState,
} from 'owa-mail-compose-store';
import createAppendOnSendBlock from '../utils/createAppendOnSendBlock';
import { lazyGetAllValidAttachments } from 'owa-attachment-well-data';
import { MentionPluginDataProviderInterface } from 'owa-editor-mentions-picker-plugin/lib/providers/MentionPluginDataProvider';
import { isFeatureEnabled } from 'owa-feature-flags';
import { ARCHIVE_FOLDERS_TREE_TYPE, SHARED_FOLDERS_TREE_TYPE } from 'owa-folders';
import { getGroupMailboxInfo } from 'owa-group-common';
import stampCLPLabelInExtendedProperty from 'owa-mail-clp/lib/utils/stampCLPLabelInExtendedProperty';
import stempMessageClassification from 'owa-mail-message-classification/lib/utils/stampMessageClassification';
import { isPublicFolderComposeViewState } from 'owa-mail-compose-actions/lib/utils/createPublicFolderComposeViewState';
import { getSelectedNode } from 'owa-mail-folder-forest-store';
import createItem from 'owa-mail-store/lib/services/createItem';
import createResponseFromModernGroup from 'owa-mail-store/lib/services/createResponseFromModernGroupItem';
import postGroupItem from 'owa-mail-store/lib/services/postGroupItem';
import PostUnifiedGroupItem from 'owa-mail-store/lib/services/PostUnifiedGroupItem';
import sendOrSaveMessageService from 'owa-mail-store/lib/services/sendOrSaveMessageService';
import { getStore } from 'owa-mail-store/lib/store/Store';
import { getDlpInformationFromViewState } from 'owa-policy-tips/lib/utils/getDlpInformationFromViewState';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import DocLink from 'owa-service/lib/contract/DocLink';
import EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import ExtendedPropertyType from 'owa-service/lib/contract/ExtendedPropertyType';
import ItemInfoResponseMessage from 'owa-service/lib/contract/ItemInfoResponseMessage';
import Message from 'owa-service/lib/contract/Message';
import PostItem from 'owa-service/lib/contract/PostItem';
import PostUnifiedGroupItemResponse from 'owa-service/lib/contract/PostUnifiedGroupItemResponse';
import approveRequestItem from 'owa-service/lib/factory/approveRequestItem';
import bodyContentType from 'owa-service/lib/factory/bodyContentType';
import itemId from 'owa-service/lib/factory/itemId';
import message from 'owa-service/lib/factory/message';
import postItem from 'owa-service/lib/factory/postItem';
import rejectRequestItem from 'owa-service/lib/factory/rejectRequestItem';
import replyAllToItem from 'owa-service/lib/factory/replyAllToItem';
import replyToItem from 'owa-service/lib/factory/replyToItem';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import SmimeBccForkingMode from 'owa-smime/lib/store/schema/SmimeBccForkingMode';
import { CANCEL_SMIME_SEND_ERROR_MESSAGE } from 'owa-smime/lib/utils/constants';
import isSmimeEnabledInViewState from 'owa-smime/lib/utils/isSmimeEnabledInViewState';
import { isItemClassSmime } from 'owa-smime/lib/utils/smimeItemClassUtils';

import {
    lazyGetSmimeAttachmentsForRequest,
    lazyTryAddSmimeProperties,
    lazyUpdateSmimeAttachmentIds,
} from 'owa-smime';

const HTML_WRAPPER =
    '<html><head><meta http-equiv="Content-Type" content="text/html; charset=UTF-8">' +
    '<style type="text/css" style="display:none;"> P {{margin-top:0;margin-bottom:0;}} </style>' +
    '</head><body dir="{0}">{1}</body></html>';

const ITEM_CLASS_NOTE = 'IPM.Note';

export interface CustomError extends Error {
    isAsyncSend?: boolean;
    showErrorToUser?: boolean;
    messageText?: string;
}

async function loadAtMentionsData(viewState: ComposeViewState, isSend: boolean, message: Message) {
    const plugins = findComposePlugins(viewState);
    // Stamp mentions related properties here, as they differ for send vs save.
    if (plugins && plugins.mentionsPickerPlugin && plugins.mentionsPickerPlugin.isImported()) {
        const pickerPlugin = await plugins.mentionsPickerPlugin.getPlugin();
        const dataProvider = pickerPlugin.dataProvider as MentionPluginDataProviderInterface;
        if (isSend) {
            const userSession = getUserConfiguration().SessionSettings;
            const mentionWrappers = dataProvider.getMentionsWrappersForSend(<EmailAddressWrapper>{
                MailboxType: 'Mailbox',
                RoutingType: 'SMTP',
                EmailAddress: userSession.UserEmailAddress,
                Name: userSession.UserDisplayName,
            });
            if (mentionWrappers && mentionWrappers.length > 0) {
                message.MentionsEx = mentionWrappers;
            }
        } else {
            const pendingTags = dataProvider.getPendingTagsForSave();
            if (pendingTags.length > 0) {
                message.PendingSocialActivityTagIds = pendingTags;
            }
        }
    }
}

function getPostForRequest(viewState: ComposeViewState): PostItem {
    let bodyValue = viewState.content;

    if (viewState.bodyType == 'HTML') {
        const textDirection =
            viewState.explicitTextDirection ||
            (getUserConfiguration().SessionSettings.IsUserCultureRightToLeft
                ? TextDirection.RTL
                : TextDirection.LTR);
        bodyValue = removeSrcAttributeFromInlineImages(bodyValue);
        bodyValue = format(HTML_WRAPPER, textDirection, bodyValue);
    }

    const body = bodyContentType({ BodyType: viewState.bodyType, Value: bodyValue });
    body.DataUriCount = getDataUriCount(viewState.content);

    let postType: PostItem;

    const commonPostFields: Partial<PostItem> = {
        Subject: viewState.subject || '',
        Importance: viewState.importance,
        Sensitivity: viewState.sensitivity,
    };

    postType = postItem({
        ...commonPostFields,
        Body: body,
    });

    if (viewState.itemId) {
        postType.ItemId = viewState.itemId;
    }

    if (viewState.fromViewState) {
        setFromInfoToPost(viewState.fromViewState, postType);
    }
    return postType;
}

/**
 * For non S/MIME mails, If there are attachments only on client
 * attach them now.
 * Scenario: Forwarding an S/MIME mail with attachments when
 * 'Always show From' setting is enabled. In that case,
 * forwarding mail is a non-S/MIME with attachments only on client.
 */
const tryAddSmimeAttachmentsInRequest = async (
    viewState: ComposeViewState
): Promise<AttachmentType[]> => {
    if (!isFeatureEnabled('cmp-smime') || isSmimeEnabledInViewState(viewState.smimeViewState)) {
        // For S/MIME compose, attachments are never saved on server.
        return null;
    }

    const smimeAttachments = (await lazyGetAllValidAttachments.import())(
        viewState.attachmentWell,
        viewState.content,
        viewState.bodyType,
        true /* filterSmimeAttachment */
    );

    return smimeAttachments && smimeAttachments.length
        ? (await lazyGetSmimeAttachmentsForRequest.import())(smimeAttachments)
        : null;
};

export function getMessageForRequest(viewState: ComposeViewState): Message {
    let bodyValue = viewState.content;
    const renameOriginalSrc = isSmimeEnabledInViewState(viewState.smimeViewState);
    if (viewState.useSmartResponse) {
        bodyValue = bodyValue + createAppendOnSendBlock(viewState.bodyType);
    }
    if (viewState.bodyType == 'HTML') {
        const textDirection =
            viewState.explicitTextDirection ||
            (getUserConfiguration().SessionSettings.IsUserCultureRightToLeft
                ? TextDirection.RTL
                : TextDirection.LTR);

        // Bug 46886: Keep the src for inline image when send
        // https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/46886
        // Limit this to normal compose scenario. For smime, I will still let the logic happen
        if (renameOriginalSrc) {
            bodyValue = removeSrcAttributeFromInlineImages(bodyValue, renameOriginalSrc);
        }
        bodyValue = format(HTML_WRAPPER, textDirection, bodyValue);
    }

    const body = bodyContentType({ BodyType: viewState.bodyType, Value: bodyValue });
    body.DataUriCount = getDataUriCount(viewState.content);

    const itemId = createItemIdForRequest(viewState.itemId);
    const referenceItemId = createItemIdForRequest(viewState.referenceItemId);

    // Set the Message recipient headers.
    // Incase of S/MIME BCC forking the appropriate headers are set according to the value of BCC forking mode set in SmimeViewState.
    let toRecipients: EmailAddressWrapper[] = [],
        ccRecipients: EmailAddressWrapper[] = [],
        bccRecipients: EmailAddressWrapper[] = [];
    if (
        isFeatureEnabled('cmp-smime') &&
        viewState.smimeViewState &&
        viewState.smimeViewState.smimeBccForkingMode != SmimeBccForkingMode.None
    ) {
        const { smimeBccForkingMode } = viewState.smimeViewState;
        switch (smimeBccForkingMode) {
            case SmimeBccForkingMode.IncludeOnlyToAndCc:
                toRecipients = getRecipientsFromWellViewState(viewState.toRecipientWell);
                ccRecipients = getRecipientsFromWellViewState(viewState.ccRecipientWell);
                break;
            case SmimeBccForkingMode.IncludeSingleBccInEachMessage:
                if (viewState.bccRecipientWell) {
                    const currentBccRecipient = viewState.smimeViewState.currentBccRecipientIndex;
                    bccRecipients = [
                        getRecipientsFromWellViewState(viewState.bccRecipientWell)[
                            currentBccRecipient
                        ],
                    ];
                }
                break;
            case SmimeBccForkingMode.IncludeAllBccInSingleMessage:
                if (viewState.bccRecipientWell) {
                    bccRecipients = getRecipientsFromWellViewState(viewState.bccRecipientWell);
                }
                break;
        }
    } else {
        toRecipients = getRecipientsFromWellViewState(viewState.toRecipientWell);
        ccRecipients = getRecipientsFromWellViewState(viewState.ccRecipientWell);
        bccRecipients = getRecipientsFromWellViewState(viewState.bccRecipientWell);
    }

    let messageType: Message;

    const commonMessageFields: Partial<Message> = {
        Subject: viewState.subject || '',
        Importance: viewState.importance,
        IsReadReceiptRequested: viewState.isReadReceiptRequested,
        IsDeliveryReceiptRequested: viewState.isDeliveryReceiptRequested,
        ToRecipients: toRecipients,
        CcRecipients: ccRecipients,
        BccRecipients: bccRecipients,
        Sensitivity: viewState.sensitivity,
    };

    let createMessageType;
    if (viewState.useSmartResponse) {
        // If the current compose form uses smart response, we always send the CreateItem request for both send and save
        // since there is not quoted body in inline compose, we could only set NewBodyContent,
        // Server side will find out the quoate body and create this item.
        createMessageType =
            viewState.operation == ComposeOperation.ReplyAll ? replyAllToItem : replyToItem;
        messageType = createMessageType({
            ...commonMessageFields,
            UpdateResponseItemId: itemId,
            ReferenceItemId: referenceItemId,
            ReferenceItemDocumentId: viewState.referenceItemDocumentId || 0,
            NewBodyContent: body,
        });
    } else if (
        viewState.operation == ComposeOperation.Approve ||
        viewState.operation == ComposeOperation.Reject
    ) {
        createMessageType =
            viewState.operation == ComposeOperation.Approve
                ? approveRequestItem
                : rejectRequestItem;
        messageType = createMessageType({
            ...commonMessageFields,
            ReferenceItemId: referenceItemId,
            Body: body,
        });
    } else {
        // If the current compose form is full compose and the compose is newly opened, there is no itemId for it
        // We use CreateItem to create this new item.
        // When the response is back from the server, the itemId will be set.
        // Next time to send or save in full compose, we could use sendOrSaveMessageService to update item.
        messageType = message({
            ...commonMessageFields,
            Body: body,
        });

        if (viewState.itemId) {
            messageType.ItemId = viewState.itemId;
        }
    }

    if (viewState.fromViewState) {
        setFromInfoToMessage(viewState.fromViewState, messageType);
    }

    return messageType;
}

function addExtendedProperty(message: Message, extendedPropertyType: ExtendedPropertyType) {
    if (message.ExtendedProperty == null) {
        message.ExtendedProperty = [extendedPropertyType];
    } else {
        //TODO: Dedupe step.
        message.ExtendedProperty.push(extendedPropertyType);
    }
}

async function sendOrSaveMessage(
    viewState: ComposeViewState,
    isSend: boolean
): Promise<ItemInfoResponseMessage> {
    let item;
    let response;

    const timeFormat = getUserConfiguration().UserOptions.TimeFormat;

    if (isPublicFolderComposeViewState(viewState)) {
        const publicFolderComposeViewState: PublicFolderComposeViewState = viewState as PublicFolderComposeViewState;
        item = getPostForRequest(viewState);
        response = await createItem(
            item,
            viewState.bodyType,
            isSend,
            timeFormat,
            viewState.isInlineCompose,
            viewState.IRMData,
            publicFolderComposeViewState.publicFolderId
        );
    } else {
        item = getMessageForRequest(viewState);

        // Prepare any extra data prior to any send or save
        item.Attachments = await tryAddSmimeAttachmentsInRequest(viewState);
        tryUpdateExtendedPropertiesForInternetHeaders(viewState, item as Message);
        tryUpdateExtendedPropertiesForAppendOnSend(viewState, item as Message, isSend);

        // CLP extended property is breaking groups api.
        // This issue doesn't exist on other platforms
        // We need to figure out what we need to do for
        // applying clp labels on group messages in OWA
        // We will ignore clp label for group scenario for now.
        if (isFeatureEnabled('cmp-clp') && !isGroupScenario(viewState)) {
            stampCLPLabelInExtendedProperty(viewState.clpViewState, item as Message);
        }

        if (isFeatureEnabled('cmp-message-classification')) {
            stempMessageClassification(viewState.classificationViewState, item as Message);
        }

        await loadAtMentionsData(viewState, isSend, item);

        // Figure out which API to use
        if (isSmimeEnabledInViewState(viewState.smimeViewState)) {
            try {
                const addSmimePropertiesRequest = getAddSmimePropertiesRequestObject(
                    viewState,
                    item,
                    isSend
                );
                await lazyTryAddSmimeProperties
                    .import()
                    .then(tryAddSmimeProperties =>
                        tryAddSmimeProperties(addSmimePropertiesRequest)
                    );
            } catch (e) {
                const error = new Error() as CustomError;
                error.message =
                    e.message == CANCEL_SMIME_SEND_ERROR_MESSAGE
                        ? CANCEL_SMIME_SEND_ERROR_MESSAGE
                        : 'SmimeEncodeError';
                return Promise.reject(error);
            }
        } else if (isFeatureEnabled('cmp-smime') && viewState.itemId) {
            // Reset S/MIME item class if user switches from S/MIME to non-S/MIME scenario
            const mailItem = getStore().items.get(viewState.itemId.Id);
            if (mailItem && isItemClassSmime(mailItem.ItemClass)) {
                item.ItemClass = ITEM_CLASS_NOTE;
            }
        }

        // DLP information is only set when the message is sent.
        if (isSend) {
            const dlpProperties = getDlpInformationFromViewState(viewState.policyTipsViewState);
            dlpProperties.map(property => addExtendedProperty(item, property));
        }

        response = await selectAndExecuteSendOrSaveApi(viewState, item, timeFormat, isSend);
    }
    clearKeysOfInternetHeadersToBeRemoved(viewState);
    const responseMessage =
        response && response.ResponseMessages && response.ResponseMessages.Items
            ? (response.ResponseMessages.Items[0] as ItemInfoResponseMessage)
            : response && !response.ResponseMessages //this is needed for PostUnifiedGroupItem, which only returns a response instead of an array.
            ? (response as ItemInfoResponseMessage)
            : null;

    if (responseMessage && responseMessage.ResponseClass == 'Success') {
        return Promise.resolve(responseMessage);
    } else {
        preHandleErrorResponse(response, viewState);
        let error: CustomError;
        if (responseMessage) {
            error = new Error(responseMessage.ResponseCode) as CustomError;
            error.messageText = responseMessage.MessageText;
        } else {
            error = new Error('Unrecognized response') as CustomError;
        }
        return Promise.reject(error);
    }
}

async function selectAndExecuteSendOrSaveApi(
    viewState: ComposeViewState,
    message: Message,
    timeFormat: string,
    isSend: boolean
) {
    if (isGroupScenario(viewState)) {
        if (isSend && viewState.operation != ComposeOperation.Forward) {
            if (
                isConsumer() ||
                viewState.itemId ||
                message.RightsManagementLicenseData ||
                viewState.IRMData
            ) {
                return await postGroupItem(
                    (<GroupComposeViewState>viewState).groupId,
                    message,
                    viewState.bodyType,
                    timeFormat,
                    viewState.IRMData,
                    viewState.isInlineCompose
                );
            }
            const groupMailboxInfo = getGroupMailboxInfo(
                (<GroupComposeViewState>viewState).groupId
            );
            return await PostUnifiedGroupItem(
                (<GroupComposeViewState>viewState).groupId,
                message,
                viewState.bodyType,
                timeFormat,
                groupMailboxInfo.requestOptions,
                viewState.isInlineCompose
            );
        } else {
            const isReply =
                viewState.operation == ComposeOperation.Reply ||
                viewState.operation == ComposeOperation.ReplyAll;

            if ((!viewState.itemId && isReply) || viewState.useSmartResponse) {
                return await createResponseFromModernGroup(
                    (<GroupComposeViewState>viewState).groupId,
                    message,
                    viewState.bodyType,
                    false /* isSend */,
                    timeFormat,
                    viewState.isInlineCompose
                );
            }
        }
    }
    const selectedNode = getSelectedNode();
    // To fix the XRF access issue, we set RemoteExecute for archive and shared folder scenarios.
    // For CreateItem, we do not set X-AnchorMailbox header, the request will always hit primary mailbox user's mailbox server.
    // On server side code, we have code fix to check the flag when reply/replyall/forward emails.
    const remoteExecute = !!(
        selectedNode &&
        (selectedNode.treeType === SHARED_FOLDERS_TREE_TYPE ||
            selectedNode.treeType === ARCHIVE_FOLDERS_TREE_TYPE)
    );

    // Set the DocLinks property for OneDrive links in the body
    if (isSend) {
        const docLinks: DocLink[] = getDocLinks(viewState);
        if (docLinks && docLinks.length > 0) {
            message.DocLinks = docLinks;
        }
    }

    return viewState.useSmartResponse || !viewState.itemId
        ? await createItem(
              message,
              viewState.bodyType,
              isSend,
              timeFormat,
              viewState.isInlineCompose,
              viewState.IRMData,
              null,
              remoteExecute
          )
        : await sendOrSaveMessageService(
              message,
              isSend,
              viewState.IRMData,
              viewState.keysOfInternetHeadersToBeRemoved
          );
}

export function sendMessage(viewState: ComposeViewState): Promise<ItemInfoResponseMessage> {
    return sendOrSaveMessage(viewState, true /*isSend*/);
}

export function saveMessage(viewState: ComposeViewState): Promise<ItemInfoResponseMessage> {
    return sendOrSaveMessage(viewState, false /*isSend*/);
}

export function handleSuccessResponse(
    responseMessage: ItemInfoResponseMessage,
    viewState: ComposeViewState
): boolean {
    // The existence of message is not a reliable determining factor of if the request has succeeded
    // Instead using ResponseClass to determine if request's success.
    if (checkIfResponseSuccess(responseMessage)) {
        const message = getMessageFromItemInfoResponseMessage(responseMessage);
        if (message) {
            if (
                isFeatureEnabled('cmp-smime') &&
                !isSmimeEnabledInViewState(viewState.smimeViewState)
            ) {
                // Check if there are any S/MIME attachments which were uploaded as part of createItem/updateItem call
                // This happens when we open a S/MIME response(r/R/f) or a S/MIME draft in non-S/MIME mode when it can't be opened in S/MIME mode.
                lazyGetAllValidAttachments.import().then(getAllValidAttachments => {
                    const smimeAttachments = getAllValidAttachments(
                        viewState.attachmentWell,
                        viewState.content,
                        viewState.bodyType,
                        true /* filterSmimeAttachment */
                    );

                    smimeAttachments.length &&
                        lazyUpdateSmimeAttachmentIds
                            .import()
                            .then(updateSmimeAttachmentIds =>
                                updateSmimeAttachmentIds(
                                    smimeAttachments,
                                    message.Attachments,
                                    itemId({ Id: viewState.composeId })
                                )
                            );
                });
            }

            if (message.ItemId) {
                updateComposeItemId(
                    viewState,
                    message.ItemId.Id,
                    message.ItemId.ChangeKey,
                    message.LastModifiedTime
                );

                if (message.Body) {
                    viewState.lastSavedMessageBody = message.Body.Value;
                }
            }
            if (message.InternetMessageId) {
                updateInternetMessageId(
                    viewState.conversationId,
                    viewState.itemId ? viewState.itemId.Id : null,
                    message.InternetMessageId
                );
            }
        }
        return true;
    }
    return false;
}

function getMessageFromItemInfoResponseMessage(responseMessage: ItemInfoResponseMessage): Message {
    // VSO 16297: responseMessage.Items is null when the request contains attachment
    // Workaround it here to check null before we use it. We can remove this check or this comment once we figure out why Items can be null
    if (responseMessage && responseMessage.Items) {
        return responseMessage.Items[0] as Message;
    } else if (
        responseMessage &&
        ((responseMessage as object) as PostUnifiedGroupItemResponse).Item
    ) {
        return ((responseMessage as object) as PostUnifiedGroupItemResponse).Item as Message;
    }

    return null;
}
